import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useUser } from '../../hooks/useUser';
import { PersonMenu } from '../../icons/PersonMenu';
import { Payment } from '../../icons/Payment';
import { Logout } from '../../icons/Logout';
import { Contact } from '../../icons/Contact';
import { Location } from '../../icons/Location';
import { ShimmerLoadingContainer } from '../loading/ShimmerLoadingContainer';
import { mediaQuery, typography } from '../../theme';
import { storage } from '../../utils/storage';
import { LOGIN_FROM_STORAGE_KEY } from '../login/useRedirectLoggedinUser';
import { MobileStaticDisplayGroupNavigation } from '../menu/menu.styles';
import { CouponsCard } from '../menu/components/CouponsCard';
import { LogoutModal } from '../header/components';
import {
  HIGHLIGHTS_DISPLAY_GROUP_ID,
  useMenuDisplayGroups,
} from '../menu/hooks/useMenuDisplayGroups';
import { Path } from '../../routes/components/path';
import { giftcardImage } from '../shoppingCart/hooks/usePaymentMethods';
import { usePathname } from 'next/navigation';
import { colors } from '../../theme/color';
import { HistoryMenuLink } from './HistoryMenuLink';
import { MenuLink } from './MenuLink';
import { LoyaltySparkles } from '../../icons/LoyaltySparkles';

type Props = {
  onClose: () => void;
};

export const MobileMenu: React.FC<Props> = ({ onClose }) => {
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  const { user } = useUser();
  const { menuDisplayGroups, isLoading: isDisplayGroupsLoading } = useMenuDisplayGroups();

  const pathname = usePathname();

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent): void => {
      if (event.keyCode === 27) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'initial';
    };
  }, [onClose]);

  const filteredProductDisplayGroups = useMemo(
    () =>
      menuDisplayGroups.filter((displayGroup) => displayGroup.id !== HIGHLIGHTS_DISPLAY_GROUP_ID),
    [menuDisplayGroups]
  );

  return (
    <Overlay onClick={() => onClose()} tabIndex={-1}>
      <Container aria-label="Mobiilivalikko" data-testid="mobilemenu" id="main-mobilemenu">
        <div>
          {logoutModalOpen && <LogoutModal onClose={() => setLogoutModalOpen(false)} />}
          <StyledCouponsCard position="Mobile Menu" small={true} />
          <StyledCouponsCard position="Mobile Menu" small={true} variant="yolo" />
          {!isDisplayGroupsLoading ? (
            <StyledMobileStaticDisplayGroupNavigation data-testid="nav-menu-categories">
              <typography.Link key={Path.GiftCard} to={Path.GiftCard} onClick={onClose}>
                <span>Lahjakortit</span>
                <img src={giftcardImage} aria-hidden="true" alt="" />
              </typography.Link>
              {filteredProductDisplayGroups.map((displayGroup) => (
                <typography.Link
                  key={displayGroup.slug}
                  to={`/menu#${displayGroup.slug}`}
                  onClick={onClose}
                >
                  <span>{displayGroup.name}</span>
                  <img src={displayGroup.iconUrl} aria-hidden="true" alt="" />
                </typography.Link>
              ))}
            </StyledMobileStaticDisplayGroupNavigation>
          ) : (
            <CategoryLoading />
          )}
          <LinkContainer>
            <div>
              {user ? (
                <>
                  <MenuLink
                    url={Path.LoyaltyBenefits}
                    onClick={onClose}
                    icon={<LoyaltySparkles />}
                    text="Edut"
                    hideCC={true}
                  />
                  <HistoryMenuLink onClick={onClose} />
                  <MenuLink
                    url={Path.AccountInformation}
                    onClick={onClose}
                    icon={<PersonMenu />}
                    text="Omat tiedot"
                  />
                  <MenuLink
                    url={Path.PaymentMethods}
                    onClick={onClose}
                    icon={<Payment />}
                    text="Omat maksutavat"
                  />
                </>
              ) : (
                <>
                  <MenuLink
                    url={Path.Loyalty}
                    onClick={onClose}
                    icon={<LoyaltySparkles />}
                    text={'Edut'}
                    hideCC={true}
                  />
                  <MenuLink
                    url={Path.Login}
                    onClick={() => {
                      storage.setItem(LOGIN_FROM_STORAGE_KEY, pathname);
                      onClose();
                    }}
                    icon={<PersonMenu />}
                    text="Kirjaudu"
                  />
                </>
              )}
            </div>
            <div>
              <MenuLink
                url={Path.Restaurants}
                onClick={onClose}
                icon={<Location />}
                text="Etsi ravintola"
              />
              <MenuLink
                url={Path.Contact}
                onClick={onClose}
                icon={<Contact />}
                text="Ota yhteyttä"
              />
              {user && (
                <MenuLink
                  onClick={() => setLogoutModalOpen(true)}
                  icon={<Logout />}
                  text="Kirjaudu ulos"
                />
              )}
            </div>
          </LinkContainer>
        </div>
      </Container>
    </Overlay>
  );
};

const CategoryLoading: React.FC = () => (
  <LoadingContainer>
    <ShimmerLoadingContainer childCount={2} size="4em" />
    <ShimmerLoadingContainer childCount={2} size="4em" />
    <ShimmerLoadingContainer childCount={2} size="4em" />
    <ShimmerLoadingContainer childCount={2} size="4em" />
    <ShimmerLoadingContainer childCount={2} size="4em" />
  </LoadingContainer>
);

const Overlay = styled.div`
  position: absolute;
  top: 4.5em;
  left: 0;
  width: 100%;
  min-height: calc(100vh - 4.5em);
  background: rgba(0, 0, 0, 0.6);
  z-index: 99999;
  overflow-y: auto;

  &:focus-visible {
    outline: none;
  }
`;

const LoadingContainer = styled.div`
  margin: 1em;

  & > div {
    margin: 0.5em;
  }
`;

const StyledMobileStaticDisplayGroupNavigation = styled(MobileStaticDisplayGroupNavigation)`
  width: 100%;
  min-width: min(100vw, 35em);
`;

const Container = styled.nav`
  @keyframes open {
    from {
      width: 0;
      height: 0;
      border-bottom-right-radius: 15000px;
    }
    to {
      width: 100vw;
      height: 100vh;
      border-bottom-right-radius: 0;
    }
  }

  position: absolute;
  top: 0;
  right: 100%;
  left: 0;
  width: 100%;
  max-width: 100%;
  min-height: calc(100vh - 4.5em);
  background: ${colors.dough.base};
  color: ${colors.textDefault};
  box-shadow: none;
  overflow: auto;
  animation: open 0.3s ease 1;

  & > div {
    max-width: 35em;
    margin: 0 auto;
    padding-bottom: env(safe-area-inset-bottom);
  }
`;

const LinkContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  min-width: min(100vw, 35em);
  padding: 0.5em 1em;

  & > div {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  @media ${mediaQuery.tiny} {
    flex-direction: column;
  }
`;

const StyledCouponsCard = styled(CouponsCard)`
  margin: 1rem 1rem 0;
`;
export { MenuLink };
